import type { OpenAPI } from "simplydo/interfaces";
import type {
  IdeaWithAssessments,
  IdeaWithOwnerChallenge,
} from "simplydo/interfaces/types/ideaTypes";

import { canEditIdea, hasPermission } from "./permissions";

export type AnonymityCheckExcludedItem =
  | { kind: "edi"; name: string }
  | { kind: "businessProfile"; name: string }
  | { kind: "managerSection"; name: string }
  | { kind: "managerField"; sectionName: string; fieldTitle: string };

export const getAnonymityCheckExcludedItems = (
  ideaTemplate: OpenAPI.Schemas["IdeaTemplate"] | undefined | null,
): AnonymityCheckExcludedItem[] => {
  if (!ideaTemplate?.body) {
    return [];
  }

  const items: AnonymityCheckExcludedItem[] = [];

  for (const section of ideaTemplate.body) {
    const sectionName = section.name?.trim() || "(Untitled section)";

    if (section.type === "edi") {
      items.push({ kind: "edi", name: sectionName });
      continue;
    }

    if (section.type === "businessProfile") {
      items.push({ kind: "businessProfile", name: sectionName });
      continue;
    }

    if (section.type === "assessment" || section.type === "impact") {
      continue;
    }

    if (section.visibility === "managers") {
      items.push({ kind: "managerSection", name: sectionName });
      continue;
    }

    for (const field of section.fields ?? []) {
      if (field.visibility === "manager") {
        items.push({
          kind: "managerField",
          sectionName,
          fieldTitle: field.title?.trim() || "(Untitled question)",
        });
      }
    }
  }

  return items;
};

export const shouldShowIdeaSection = (
  idea: IdeaWithAssessments | undefined,
  section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number],
) => {
  if (!idea || !section) return false;

  if (section.type === "assessment") {
    return idea.ownerIdeaTemplate?.assessmentEnabled;
  }

  return (
    section.fields?.length > 0 ||
    section.type === "businessProfile" ||
    section.type === "impact" ||
    section.type === "edi"
  );
};

export const shouldShowIdeaEdiSection = (
  user: OpenAPI.Schemas["User"] | undefined,
  idea: IdeaWithOwnerChallenge | undefined,
  section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number],
) => {
  if (!idea || !section) return false;
  if (canEditIdea(user, idea)) return true;
  if (
    section.type === "edi" &&
    hasPermission(user, "org.viewEdi", idea.ownerChallenge?.organisation)
  )
    return true;
  return false;
};
